import {parsePhoneNumber} from 'libphonenumber-js';

/**
 * Calculate age based on the given date of birth.
 *
 * @param {string} dateOfBirth - The date of birth in the format "YYYY-MM-DD".
 * @returns {number} The calculated age.
 */
export const calculateAge = dateOfBirth => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};

/**
 * Validation Limits for Weight and Height based on the unit of measurement.
 */

export const VALIDATION_LIMITS = {
  WEIGHT: {
    KG: { MIN: 30, MAX: 300 },
    LBS: { MIN: 66, MAX: 661 }
  },
  HEIGHT: {
    CM: { MIN: 120, MAX: 220 },
    FT_IN: {
      MIN: { FT: 4, IN: 0 }, // 3'11"
      MAX: { FT: 7, IN: 0 }   // 7'2"
    }
  }
};

/**
 * Calculate the duration in days between the given start date and the current date.
 *
 * @param {string} startDate - The start date in the format "YYYY-MM-DD".
 * @returns {number} The duration in days.
 */
export const calculateDuration = startDate => {
  const oneDay = 24 * 60 * 60 * 1000; // One day in milliseconds
  const start = new Date(startDate);
  const end = new Date();

  const durationDays = Math.round(Math.abs((end - start) / oneDay));

  return durationDays;
};

/**
 * Generate a random hex color code.
 *
 * @returns {string} A random hex color code.
 */
export const getRandomHexColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

/**
 * Get the number of lines in blue based on the result level.
 *
 * @param {string} level - The result level ("Low", "Fair", "Moderate", "Strong").
 * @returns {number} The number of lines in blue.
 */
export const getTotalLinesColourBlue = level => {
  let numLinesInBlue = 0;
  switch (level) {
    case 'Strong':
      numLinesInBlue = 4;
      break;
    case 'Moderate':
      numLinesInBlue = 3;
      break;
    case 'Fair':
      numLinesInBlue = 2;
      break;
    case 'Low':
      numLinesInBlue = 1;
      break;
  }

  return numLinesInBlue;
};

/**
 * Get the next theme hex color from a predefined list.
 *
 * @returns {string} The next theme hex color.
 */
export const getThemeHexColor = (() => {
  const themeColors = ['#30A1F2', '#35C6C9', '#A6E177'];
  let currentIndex = 0;

  return () => {
    const currentColor = themeColors[currentIndex];
    currentIndex = (currentIndex + 1) % themeColors.length;
    return currentColor;
  };
})();

/**
 * Get the operating system of the user's device.
 *
 * @returns {string} The operating system ("WINDOWS", "Windows Phone", "Android", "IPAD", "IPHONE", "IPOD", "Mac OS", "LINUX", "Unknown").
 */
export const getOperatingSystem = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (/windows/i.test(userAgent)) {
    return 'WINDOWS';
  }

  if (/windows phone/i.test(userAgent)) {
    return 'Windows Phone';
  }

  if (/android/i.test(userAgent)) {
    return 'Android';
  }
  if (/iPad/.test(userAgent) && !window.MSStream) {
    return 'IPAD';
  }

  if (/iPhone/.test(userAgent) && !window.MSStream) {
    return 'IPHONE';
  }

  if (/iPod/.test(userAgent) && !window.MSStream) {
    return 'IPOD';
  }

  if (/Mac/.test(navigator.platform)) {
    return 'Mac OS';
  }

  if (/Linux/.test(navigator.platform)) {
    return 'LINUX';
  }

  return 'Unknown';
};

export const isEmailValid = email => {
  // according to this page: https://www.regular-expressions.info/email.html it matches 99% of all email addresses used
  const superEmailRegexMatcher = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return superEmailRegexMatcher.test(email);
};

/**
 * Masks a phone number by replacing all but the last 4 digits with asterisks.
 *
 * @param {string} phoneNumber - The phone number to be masked, must be a string in E.164 format otherwise trash in trash out.
 * @returns {string} The masked phone number. if it is not in the right format then it will return the same number without masking
 */
export const maskPhoneNumber = phoneNumber => {
  // Check if the phone number is valid and contains at least 4 digits
  let parsedPhoneNumber;
  try {
    parsedPhoneNumber = parsePhoneNumber(phoneNumber);
  } catch (error) {
    // Handle any parsing errors
    console.error(`Error parsing phone number ${phoneNumber} :`, error);
    return phoneNumber;
  }

  if (parsedPhoneNumber === undefined) return phoneNumber;

  if (parsedPhoneNumber.countryCallingCode === '1') {
    const maskedPhoneNumber = '***-***-' + phoneNumber.substring(phoneNumber.length - 4);
    return maskedPhoneNumber;
  }
  // this would be for numbers that do not have a country calling code of 1
  const wholeNumber = parsedPhoneNumber.number;

  // Extract the last 4 digits
  const last4Digits = wholeNumber.slice(-4);

  // Create a mask with asterisks (*) for the remaining digits, one more because of the + sign
  const maskedDigits = '*'.repeat(wholeNumber.length - 5);

  // Combine the masked and unmasked parts
  const maskedPhoneNumber = '+' + maskedDigits + last4Digits;

  return maskedPhoneNumber;
};

/**
 * Masks the email address by replacing characters before the '@' symbol with asterisks.
 * It leaves the first and last characters of the username without asterisks if the user part of
 * the email is greater than 2 characters. if not all the user part will be masked
 *
 * @param {string} email - The email address to be masked.
 * @returns {string} - The masked email address.
 */
export const maskEmail = email => {
  const atIndex = email.indexOf('@');
  if (atIndex >= 3) {
    const maskedPart = email.substring(1, atIndex - 1).replace(/./g, '*');
    return email[0] + maskedPart + email.substring(atIndex - 1);
  }
  return '*'.repeat(atIndex) + email.substring(atIndex);
};

/**
 * Convert snake_case to Capitalized Case.
 *
 * @param {string} snake_case - The input string in snake_case.
 * @returns {string} The converted string in Capitalized Case.
 */
export const snakeCaseToCapitalizedCase = snake_case => {
  let output = snake_case.replaceAll('_', ' ').split(' ');
  output = output.reduce((almost, word) => {
    if (almost === output[0]) return `${word.substring(0, 1).toUpperCase() + word.substring(1)}`;
    return (almost += ` ${word.substring(0, 1).toUpperCase() + word.substring(1)}`);
  }, output[0]);
  return output;
};

/**
 * Convert words to Capitalized Case.
 *
 * @param {string} stringInput - The input string with words.
 * @returns {string} The converted string in Capitalized Case.
 */
export const wordsToCapitalizedCase = stringInput => {
  let output = stringInput.split(/\s+/).reduce((almost, word) => {
    almost += `${word.substring(0, 1).toUpperCase() + word.substring(1)} `;
    return almost;
  }, '');
  return output;
};

/**
 * Delays the execution of code by a specified number of seconds.
 *
 * @param {number} seconds - The number of seconds to delay the execution.
 * @returns {Promise} - A promise that resolves after the specified delay.
 *
 * @throws {TypeError} - If the seconds parameter is not a number.
 * @throws {RangeError} - If the seconds parameter is a negative number.
 *
 * @example
 * // Delay execution by 2 seconds
 * delay(2).then(() => {
 *   console.log('Code execution delayed by 2 seconds');
 * });
 */
export const delay = seconds => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, seconds * 1000);
  });
};

/**
 * Rounds a number up to a specified decimal point.
 * @param {number} number - The number to be rounded.
 * @param {number} decimalPoints - The desired decimal points.
 * @returns {number} The rounded number.
 */
export const roundToDecimal = (number, decimalPoints) => {
  return Math.round(number * Math.pow(10, decimalPoints)) / Math.pow(10, decimalPoints);
};

/**
 * Rounds a number up to a specified decimal point.
 * @param {number} number - The number to be truncateed.
 * @param {number} decimalPoints - The desired decimal points.
 * @returns {number} The truncateed number.
 */
export const truncateToDecimal = (number, decimalPoints) => {
  return Math.trunc(number * Math.pow(10, decimalPoints)) / Math.pow(10, decimalPoints);
};

/**
 * Calculates the size of a base64 encoded string from the size of its binary representation, all in megabtes.
 *
 * @param {number} MBSize - The size of the binary representation in megabytes.
 * @returns {number} The size of the base64 encoded string in kilobytes.
 * @throws {TypeError} If the MBSize parameter is not a number.
 * @throws {RangeError} If the MBSize parameter is less than or equal to zero.
 */
export const calculateBase64SizeFromBinarySize = MBSize => {
  if (typeof MBSize !== 'number') {
    throw new TypeError('MBSize parameter must be a number');
  }

  if (MBSize <= 0) {
    throw new RangeError('MBSize parameter must be greater than zero');
  }

  const KBSize = MBSize * 1000;
  const Base64KBSize = 4 * Math.ceil(KBSize / 3);
  return truncateToDecimal(Base64KBSize, 1);
};

/**
 * Calculates the megabytes size of a binary file based on its output size as a base64 encoded file
 *
 * @param {number} MBSize - The size in megabytes (MB).
 * @returns {number} The calculated binary size in megabytes (MB).
 * @throws {TypeError} If the input is not a number.
 * @throws {RangeError} If the input is negative.
 */
export const calculateBinarySizeFromBase64Size = MBSize => {
  if (typeof MBSize !== 'number') {
    throw new TypeError('MBSize must be a number');
  }

  if (MBSize < 0) {
    throw new RangeError('MBSize must be a positive number');
  }
  const KBSize = MBSize * 1000;
  const result = Math.floor(((KBSize - 3) * 3) / 4 / 4) * 4;
  return truncateToDecimal(result / 1000, 1);
};

/**
 * Retrieves the duration of an audio file.
 *
 * @param {string} objectURL - The object URL of the audio file.
 * @returns {Promise<number>} - A Promise that resolves with the duration of the audio file in seconds.
 * @throws {Error} - If there is an error loading the audio file.
 */
export const getAudioDuration = objectURL => {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.addEventListener('loadedmetadata', () => {
      const duration = audio.duration;
      resolve(duration);
    });
    audio.addEventListener('error', error => {
      reject(error);
    });
    audio.src = objectURL;
  });
};

/**
 * Checks if a given URL is a valid object URL.
 *
 * @param {string} url - The URL to be checked.
 * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating whether the URL is valid or not.
 * @throws {Error} - If there is an error while fetching the URL.
 *
 */
export const isValidObjectURL = async url => {
  try {
    const response = await fetch(url);
    return response.ok;
  } catch (error) {
    return false;
  }
};
